import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

// MUI Joy Components
import {
    Breadcrumbs, Link, Card, CircularProgress, Alert, Stack, Divider,
    Box, Typography, Grid, Sheet, Table, Chip, Tooltip, Modal, ModalDialog,
    ModalClose, IconButton, List, ListItem, ListItemContent,
} from "@mui/joy";

// Icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ReportIcon from '@mui/icons-material/Report';
import GroupIcon from '@mui/icons-material/Group';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Local Imports
import ApiService from "../../axios/Bookings-Axios";

function StatCard({ icon, title, value, color = 'primary' }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, bgcolor: `${color}.softBg`, color: `${color}.solidColor`
            }}>
                {icon}
            </Box>
            <Box>
                <Typography level="body-sm">{title}</Typography>
                <Typography level="h3">{value}</Typography>
            </Box>
        </Box>
    );
}

function formatCurrency(amount: number) {
    return amount.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
}

// ── Week Detail Modal ──
function WeekDetailModal({ selectedWeek, salespersonUsername, onClose }) {
    if (!selectedWeek) return null;

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{
                maxWidth: { xs: '95vw', sm: 560 },
                width: { xs: '95vw', sm: 'auto' },
                maxHeight: '85vh',
                overflow: 'auto'
            }}>
                <ModalClose />
                <Typography level="h4" sx={{ mb: 0.5 }}>
                    {salespersonUsername} — {selectedWeek?.label}
                </Typography>
                <Typography level="body-xs" color="neutral" sx={{ mb: 2 }}>
                    Commission breakdown
                </Typography>

                {/* Bookings */}
                {selectedWeek?.bookingDetails?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography level="title-md" sx={{ mb: 1 }}>Bookings ({selectedWeek.bookingDetails.length})</Typography>
                        <List size="sm">
                            {selectedWeek.bookingDetails.map((booking, i) => (
                                <ListItem key={i}>
                                    <ListItemContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography level="body-sm" fontWeight={500}>
                                                    {booking.customerName}
                                                    {booking.isFranchiseOwner && (
                                                        <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                            (franchise owner)
                                                        </Typography>
                                                    )}
                                                    {booking.isSplit && <Chip size="sm" color="warning" variant="soft" sx={{ ml: 1 }}>Split</Chip>}
                                                    {booking.isOverrideSplit && <Chip size="sm" color="primary" variant="soft" sx={{ ml: 0.5 }}>Override</Chip>}
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    {new Date(booking.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                    {booking.bookingValue > 0 && ` · Booking value: ${formatCurrency(booking.bookingValue)}`}
                                                </Typography>
                                            </Box>
                                            <Typography level="body-sm" color="success">
                                                {formatCurrency(booking.commission)}
                                            </Typography>
                                        </Box>
                                    </ListItemContent>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Split Bookings (Assist) */}
                {selectedWeek?.splitBookingDetails?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography level="title-md" sx={{ mb: 1 }}>Split Bookings (Assist) ({selectedWeek.splitBookingDetails.length})</Typography>
                        <List size="sm">
                            {selectedWeek.splitBookingDetails.map((booking, i) => (
                                <ListItem key={i}>
                                    <ListItemContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography level="body-sm" fontWeight={500}>
                                                    {booking.customerName}
                                                    {booking.isFranchiseOwner && (
                                                        <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                            (franchise owner)
                                                        </Typography>
                                                    )}
                                                    {booking.isOverrideSplit && <Chip size="sm" color="primary" variant="soft" sx={{ ml: 0.5 }}>Override</Chip>}
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    {new Date(booking.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </Typography>
                                                {booking.isOverrideSplit && booking.overrideAmount > 0 && (
                                                    <Typography level="body-xs" color="primary">
                                                        Split: {formatCurrency(booking.commission)} + Override: {formatCurrency(booking.overrideAmount)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography level="body-sm" color={booking.isOverrideSplit ? 'success' : 'warning'}>
                                                {formatCurrency(booking.commission + (booking.overrideAmount || 0))}
                                            </Typography>
                                        </Box>
                                    </ListItemContent>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Cancelled */}
                {selectedWeek?.cancelledBookings?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography level="title-md" sx={{ mb: 1 }} color="warning">Cancelled ({selectedWeek.cancelledBookings.length})</Typography>
                        <List size="sm">
                            {selectedWeek.cancelledBookings.map((b, i) => (
                                <ListItem key={i}><ListItemContent>
                                    <Typography level="body-sm" fontWeight={500}>
                                        {b.customerName}
                                        {b.isFranchiseOwner && (
                                            <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                (franchise owner)
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                        Created {new Date(b.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                        {b.cancelledAt && ` · Cancelled ${new Date(b.cancelledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`}
                                    </Typography>
                                </ListItemContent></ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Deleted */}
                {selectedWeek?.deletedBookings?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography level="title-md" sx={{ mb: 1 }} color="danger">Deleted ({selectedWeek.deletedBookings.length})</Typography>
                        <List size="sm">
                            {selectedWeek.deletedBookings.map((b, i) => (
                                <ListItem key={i}><ListItemContent>
                                    <Typography level="body-sm" fontWeight={500}>
                                        {b.customerName}
                                        {b.isFranchiseOwner && (
                                            <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                (franchise owner)
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                        Created {new Date(b.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                    </Typography>
                                </ListItemContent></ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Deductions */}
                {selectedWeek?.deductionDetails?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography level="title-md" sx={{ mb: 1 }} color="danger">Deductions ({selectedWeek.deductionDetails.length})</Typography>
                        <List size="sm">
                            {selectedWeek.deductionDetails.map((d, i) => (
                                <ListItem key={i}><ListItemContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography level="body-sm" fontWeight={500}>{d.customerName}</Typography>
                                            <Typography level="body-xs" color="neutral">
                                                Cancelled {new Date(d.cancelledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                            </Typography>
                                        </Box>
                                        <Typography level="body-sm" color="danger">-{formatCurrency(d.amount)}</Typography>
                                    </Box>
                                </ListItemContent></ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Empty state */}
                {!selectedWeek?.bookingDetails?.length && !selectedWeek?.splitBookingDetails?.length &&
                 !selectedWeek?.cancelledBookings?.length && !selectedWeek?.deletedBookings?.length &&
                 !selectedWeek?.deductionDetails?.length && (
                    <Typography level="body-sm" color="neutral">No bookings for this week.</Typography>
                )}
            </ModalDialog>
        </Modal>
    );
}


// ── Weekly Payroll Table ──
function WeeklyPayrollTable({ weeklyData, salespersonUsername }) {
    const [selectedWeek, setSelectedWeek] = useState(null);

    if (!weeklyData || weeklyData.length === 0) return null;

    return (
        <>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                <Table size="sm" hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: '30%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                    Week (Sat - Fri)
                                </Box>
                            </th>
                            <th style={{ textAlign: 'center' }}>Bookings</th>
                            <th style={{ textAlign: 'center' }}>Split</th>
                            <th style={{ textAlign: 'right' }}>Commission</th>
                            <th style={{ textAlign: 'right' }}>Override</th>
                            <th style={{ textAlign: 'center' }}>Cancellations</th>
                            <th style={{ textAlign: 'right', fontWeight: 700 }}>Total to Pay</th>
                            <th style={{ textAlign: 'center', width: 50 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeklyData.map((week, idx) => (
                            <tr key={idx} style={{ backgroundColor: week.isCurrent ? 'var(--joy-palette-warning-softBg)' : undefined }}>
                                <td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography level="body-sm" fontWeight={week.isCurrent ? 600 : 400}>
                                            {week.label}
                                        </Typography>
                                        {week.isCurrent && (
                                            <Chip size="sm" color="warning" variant="soft">Current</Chip>
                                        )}
                                    </Box>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <Typography level="body-sm">{week.bookingsCompleted}</Typography>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <Typography level="body-sm" color={week.splitBookings > 0 ? 'warning' : 'neutral'}>
                                        {week.splitBookings || 0}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <Typography level="body-sm" color="success">
                                        {formatCurrency(week.commissionEarned)}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {week.overrideEarned > 0 ? (
                                        <Tooltip
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography level="body-xs" fontWeight={600} sx={{ mb: 0.5 }}>Override Breakdown:</Typography>
                                                    {week.overrideDetails.map((d, i) => (
                                                        <Typography key={i} level="body-xs">
                                                            {d.username}: {d.commissions} booking{d.commissions !== 1 ? 's' : ''} = {formatCurrency(d.overrideAmount)}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            }
                                            placement="left" arrow variant="outlined"
                                        >
                                            <Box component="span" sx={{ display: 'inline-block' }}>
                                                <Typography level="body-sm" color="primary" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                                                    {formatCurrency(week.overrideEarned)}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Typography level="body-sm" color="neutral">—</Typography>
                                    )}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    {(() => {
                                        const total = (week.cancelledBookings?.length || 0) + (week.deletedBookings?.length || 0);
                                        const hasDeductions = week.deductionDetails?.length > 0;
                                        if (total > 0 || hasDeductions) {
                                            return (
                                                <Tooltip
                                                    title={
                                                        <Box sx={{ p: 0.5 }}>
                                                            {(week.cancelledBookings?.length || 0) > 0 && <Typography level="body-xs">{week.cancelledBookings.length} cancelled</Typography>}
                                                            {(week.deletedBookings?.length || 0) > 0 && <Typography level="body-xs">{week.deletedBookings.length} deleted</Typography>}
                                                            {hasDeductions && <Typography level="body-xs" color="danger">{week.deductionDetails.length} deduction{week.deductionDetails.length !== 1 ? 's' : ''}</Typography>}
                                                        </Box>
                                                    }
                                                    placement="left" arrow variant="outlined"
                                                >
                                                    <Typography level="body-sm" color={hasDeductions ? 'danger' : 'warning'} sx={{ cursor: 'help' }}>
                                                        {total + (week.deductionDetails?.length || 0)}
                                                    </Typography>
                                                </Tooltip>
                                            );
                                        }
                                        return <Typography level="body-sm" color="neutral">—</Typography>;
                                    })()}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <Typography level="body-sm" fontWeight={700} color={week.totalEarned > 0 ? 'success' : 'neutral'}>
                                        {formatCurrency(week.totalEarned)}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <IconButton size="sm" variant="plain" color="primary" onClick={() => setSelectedWeek(week)}>
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            {selectedWeek && (
                <WeekDetailModal
                    selectedWeek={selectedWeek}
                    salespersonUsername={salespersonUsername}
                    onClose={() => setSelectedWeek(null)}
                />
            )}
        </>
    );
}

function SalesPersonCard({ username, data }) {
    const { weeklyPayroll, summary } = data;

    return (
        <Card variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography level="h3" component="h2" startDecorator={<GroupIcon color="primary" />}>
                        {username}
                    </Typography>
                    {data.isFranchiseOwner && (
                        <Typography component="span" level="body-sm" sx={{ color: 'neutral.400', fontWeight: 400 }}>
                            (franchise owner)
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography level="body-xs" color="neutral">4-Week Total</Typography>
                        <Typography level="h4" color="success" fontWeight={700}>
                            {formatCurrency(summary.totalEarned)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid xs={6} sm={2}>
                    <StatCard icon={<TaskAltRoundedIcon />} title="Bookings" value={summary.totalBookings} color="primary" />
                </Grid>
                <Grid xs={6} sm={2}>
                    <StatCard icon={<CallSplitIcon />} title="Split" value={summary.totalSplitBookings || 0} color="warning" />
                </Grid>
                <Grid xs={6} sm={3}>
                    <StatCard icon={<MonetizationOnRoundedIcon />} title="Commission" value={formatCurrency(summary.totalCommission)} color="success" />
                </Grid>
                <Grid xs={6} sm={2}>
                    <StatCard icon={<TrendingUpIcon />} title="Overrides" value={formatCurrency(summary.totalOverride)} color="warning" />
                </Grid>
                <Grid xs={12} sm={3}>
                    <StatCard icon={<PaymentsIcon />} title="Total Earned" value={formatCurrency(summary.totalEarned)} color="success" />
                </Grid>
            </Grid>

            {/* Weekly Payroll Table */}
            <Typography level="title-md" sx={{ mb: 1 }} startDecorator={<CalendarTodayIcon />}>
                Weekly Payroll (Last 4 Weeks)
            </Typography>
            <WeeklyPayrollTable
                weeklyData={weeklyPayroll}
                salespersonUsername={username}
            />
        </Card>
    );
}

export default function SalesTeam() {
    const [salesData, setSalesData] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const apiResponse = await ApiService.getAllFranchiseSalesTeamStats();
            if (apiResponse && Object.keys(apiResponse).length > 0) {
                setSalesData(apiResponse);
            } else {
                setSalesData({});
            }
        } catch (err) {
            setError('Failed to fetch sales data. Please ensure you have permission to view this franchise.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const sortedUsers = Object.entries(salesData)
        .filter(([key, val]: any) => val?.summary)
        .sort((a: any, b: any) => {
            return (b[1].summary?.totalEarned || 0) - (a[1].summary?.totalEarned || 0);
        });

    return (
        <>
            {/* --- HEADER --- */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Breadcrumbs size="sm" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard"><HomeRoundedIcon /></Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>Sales Payroll</Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ my: 2 }}>
                <Typography level="h2" component="h1">Sales Team Payroll</Typography>
                <Typography level="body-sm" color="neutral">
                    Weekly commission breakdown for payroll (Saturday to Friday)
                </Typography>
            </Box>

            {/* --- MAIN CONTENT --- */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : error ? (
                <Alert color="danger" variant="soft" startDecorator={<ReportIcon />}>{error}</Alert>
            ) : Object.keys(salesData).length === 0 ? (
                <Alert color="neutral" variant="soft">No sales activity found in the last 4 weeks.</Alert>
            ) : (
                <Stack spacing={3}>
                    {sortedUsers.map(([username, userData]) => (
                        <SalesPersonCard
                            key={username}
                            username={username}
                            data={userData}
                        />
                    ))}
                </Stack>
            )}
        </>
    );
}
